import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTCoins, TCOINS_TYPES } from '../../context/TCoinsContext';
import { useTheme } from '../../context/ThemeContext';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

/**
 * คำนวณโบนัสตาม streak
 * Day 1: 10 coins
 * Day 2: 15 coins (+5)
 * Day 3: 20 coins (+5)
 * Day 4: 25 coins (+5)
 * Day 5: 30 coins (+5)
 * Day 6: 35 coins (+5)
 * Day 7: 50 coins (+15) - Milestone!
 * Day 8: 40 coins (+5)
 * ...
 * Day 14: 100 coins - Milestone!
 * Day 30: 200 coins - Milestone!
 */
const calculateDailyBonus = (streak) => {
  if (streak === 0) return 10; // First day
  if (streak === 7) return 50; // 7-day milestone
  if (streak === 14) return 100; // 14-day milestone
  if (streak === 30) return 200; // 30-day milestone
  if (streak > 30) return 200 + (streak - 30) * 5; // After 30 days, +5 per day
  
  // Normal progression: 10 + (streak * 5)
  return 10 + (streak * 5);
};

/**
 * ตรวจสอบว่าเป็น milestone หรือไม่
 */
const isMilestone = (streak) => {
  return streak === 7 || streak === 14 || streak === 30 || streak % 30 === 0;
};

/**
 * Calendar View - แสดง 7 วันล่าสุด
 */
const CalendarView = ({ streak, lastClaimDate, currentTheme }) => {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // สร้าง 7 วันย้อนหลัง
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Handle both Firestore Timestamp and Date object
    const lastClaim = lastClaimDate 
      ? (lastClaimDate.toDate ? lastClaimDate.toDate() : 
         (lastClaimDate instanceof Date ? lastClaimDate : null))
      : null;
    const isClaimed = lastClaim && 
      date.getDate() === lastClaim.getDate() &&
      date.getMonth() === lastClaim.getMonth() &&
      date.getFullYear() === lastClaim.getFullYear();
    
    const isToday = date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
    
    days.push({
      date,
      isClaimed,
      isToday,
      dayName: date.toLocaleDateString('th-TH', { weekday: 'short' }),
      dayNumber: date.getDate()
    });
  }
  
  return (
    <div className="grid grid-cols-7 gap-2 mb-6">
      {days.map((day, index) => (
        <div
          key={index}
          className={`flex flex-col items-center p-2 rounded-lg transition-all ${
            day.isToday
              ? 'text-white scale-110 shadow-lg'
              : day.isClaimed
              ? 'text-white'
              : ''
          }`}
          style={{
            backgroundColor: day.isToday
              ? currentTheme.colors.primary
              : day.isClaimed
              ? currentTheme.colors['secondary-bg']
              : currentTheme.colors['border-color'],
            color: day.isToday
              ? '#FFFFFF'
              : day.isClaimed
              ? currentTheme.colors['text-primary']
              : currentTheme.colors['text-secondary']
          }}
        >
          <div className="text-xs font-medium mb-1">{day.dayName}</div>
          <div className="text-lg font-bold">{day.dayNumber}</div>
          {day.isClaimed && (
            <div className="text-xs mt-1">✓</div>
          )}
          {day.isToday && !day.isClaimed && (
            <div className="text-xs mt-1 animate-pulse">🎁</div>
          )}
        </div>
      ))}
    </div>
  );
};

const DailyBonusModal = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const { currentTheme } = useTheme();
  const { addTCoins, loadTCoins } = useTCoins();
  const [streak, setStreak] = useState(0);
  const [lastClaimDate, setLastClaimDate] = useState(null);
  const [canClaim, setCanClaim] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [claimedAmount, setClaimedAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && currentUser) {
      loadStreakData();
    }
  }, [isOpen, currentUser]);

  const loadStreakData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const streakData = userData.dailyBonusStreak || {};
        
        // ตรวจสอบ streak
        const lastClaim = streakData.lastClaimDate?.toDate?.() || 
                         (streakData.lastClaimDate instanceof Date ? streakData.lastClaimDate : null);
        const currentStreak = streakData.currentStreak || 0;
        const lastStreakDate = streakData.lastStreakDate?.toDate?.() || 
                              (streakData.lastStreakDate instanceof Date ? streakData.lastStreakDate : null);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // ตรวจสอบว่า claim วันนี้แล้วหรือยัง
        const claimedToday = lastClaim && 
          lastClaim.getDate() === today.getDate() &&
          lastClaim.getMonth() === today.getMonth() &&
          lastClaim.getFullYear() === today.getFullYear();
        
        // ตรวจสอบว่า streak ยังต่อเนื่องหรือไม่
        let newStreak = currentStreak;
        if (lastStreakDate) {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          
          const isConsecutive = 
            lastStreakDate.getDate() === yesterday.getDate() &&
            lastStreakDate.getMonth() === yesterday.getMonth() &&
            lastStreakDate.getFullYear() === yesterday.getFullYear();
          
          if (!isConsecutive && !claimedToday) {
            // Reset streak ถ้าไม่ต่อเนื่อง
            newStreak = 0;
          }
        }
        
        setStreak(newStreak);
        setLastClaimDate(lastClaim);
        setCanClaim(!claimedToday);
      }
    } catch (error) {
      console.error('Error loading streak data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!canClaim || isClaiming || !currentUser) return;
    
    setIsClaiming(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const streakData = userData.dailyBonusStreak || {};
        const currentStreak = streakData.currentStreak || 0;
        
        // คำนวณ streak ใหม่
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const lastStreakDate = streakData.lastStreakDate?.toDate?.() || 
                              (streakData.lastStreakDate instanceof Date ? streakData.lastStreakDate : null);
        
        let newStreak = 0;
        if (lastStreakDate) {
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          
          const isConsecutive = 
            lastStreakDate.getDate() === yesterday.getDate() &&
            lastStreakDate.getMonth() === yesterday.getMonth() &&
            lastStreakDate.getFullYear() === yesterday.getFullYear();
          
          newStreak = isConsecutive ? currentStreak + 1 : 1;
        } else {
          newStreak = 1;
        }
        
        // คำนวณโบนัส
        const bonusAmount = calculateDailyBonus(newStreak - 1); // -1 เพราะจะเพิ่ม streak หลังจาก claim
        
        // เพิ่ม T Coins (Bonus Coins)
        await addTCoins(currentUser.uid, TCOINS_TYPES.VIRTUAL, bonusAmount, `Daily Login Bonus - Day ${newStreak}`);
        
        // อัพเดท streak data
        await updateDoc(userRef, {
          'dailyBonusStreak.lastClaimDate': Timestamp.now(),
          'dailyBonusStreak.currentStreak': newStreak,
          'dailyBonusStreak.lastStreakDate': Timestamp.now(),
          'dailyBonusStreak.totalClaimed': (streakData.totalClaimed || 0) + bonusAmount
        });
        
        setStreak(newStreak);
        setLastClaimDate(today);
        setCanClaim(false);
        setClaimedAmount(bonusAmount);
        setShowSuccess(true);
        
        // Reload T Coins
        await loadTCoins(currentUser.uid);
        
        // Auto close after 3 seconds
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 3000);
      }
    } catch (error) {
      console.error('Error claiming daily bonus:', error);
      alert('เกิดข้อผิดพลาดในการรับโบนัส: ' + error.message);
    } finally {
      setIsClaiming(false);
    }
  };

  if (!isOpen) return null;

  const bonusAmount = calculateDailyBonus(streak);
  const isMilestoneDay = isMilestone(streak + 1); // +1 เพราะจะ claim วันนี้
  const nextMilestone = streak < 7 ? 7 : streak < 14 ? 14 : streak < 30 ? 30 : (Math.floor(streak / 30) + 1) * 30;
  const daysToMilestone = nextMilestone - streak;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl relative"
        style={{ backgroundColor: currentTheme.colors.background }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 transition-colors"
          style={{ color: currentTheme.colors['text-secondary'] }}
          onMouseEnter={(e) => e.target.style.color = currentTheme.colors['text-primary']}
          onMouseLeave={(e) => e.target.style.color = currentTheme.colors['text-secondary']}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {loading ? (
          <div className="p-8 text-center">
            <div 
              className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
              style={{ borderColor: currentTheme.colors.primary }}
            ></div>
            <p style={{ color: currentTheme.colors['text-secondary'] }}>กำลังโหลด...</p>
          </div>
        ) : showSuccess ? (
          /* Success Animation */
          <div className="p-8 text-center">
            <div className="text-6xl mb-4 animate-bounce">🎉</div>
            <h2 
              className="text-2xl font-bold mb-2"
              style={{ color: currentTheme.colors.primary }}
            >
              รับโบนัสสำเร็จ!
            </h2>
            <div 
              className="text-4xl font-bold mb-4"
              style={{ color: currentTheme.colors.accent }}
            >
              +{claimedAmount.toLocaleString()} 🎁
            </div>
            {isMilestone(streak) && (
              <div 
                className="rounded-lg p-4 mb-4 border-2"
                style={{
                  backgroundColor: currentTheme.colors['secondary-bg'],
                  borderColor: currentTheme.colors.accent
                }}
              >
                <div className="text-2xl mb-2">🏆</div>
                <div 
                  className="font-bold"
                  style={{ color: currentTheme.colors['text-primary'] }}
                >
                  Milestone {streak} วัน!
                </div>
                <div 
                  className="text-sm"
                  style={{ color: currentTheme.colors['text-secondary'] }}
                >
                  รางวัลพิเศษ!
                </div>
              </div>
            )}
            <p style={{ color: currentTheme.colors['text-secondary'] }}>
              Streak: {streak} วัน 🔥
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div 
              className="p-6 rounded-t-2xl text-white relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`
              }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white bg-opacity-20 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white bg-opacity-20 rounded-full -ml-12 -mb-12"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">🎁 Daily Bonus</h2>
                    <p className="text-sm opacity-90">รับโบนัสทุกวัน!</p>
                  </div>
                  {streak > 0 && (
                    <div className="bg-white bg-opacity-20 backdrop-blur-sm px-4 py-2 rounded-full">
                      <div className="text-xs opacity-90">Streak</div>
                      <div className="text-xl font-bold">🔥 {streak}</div>
                    </div>
                  )}
                </div>

                {/* Bonus Amount Display */}
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-4 mb-4">
                  <div className="text-sm opacity-90 mb-1">โบนัสวันนี้</div>
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-bold">
                      {bonusAmount.toLocaleString()} 🎁
                    </div>
                    {isMilestoneDay && (
                      <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                        🏆 MILESTONE!
                      </div>
                    )}
                  </div>
                  {streak > 0 && (
                    <div className="text-xs opacity-75 mt-2">
                      Base: 10 + Streak Bonus: {(bonusAmount - 10).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Calendar View */}
              <div className="mb-6">
                <h3 
                  className="text-lg font-semibold mb-3"
                  style={{ color: currentTheme.colors['text-primary'] }}
                >
                  📅 ประวัติ 7 วันล่าสุด
                </h3>
                <CalendarView streak={streak} lastClaimDate={lastClaimDate} currentTheme={currentTheme} />
              </div>

              {/* Milestone Progress */}
              {streak < nextMilestone && (
                <div 
                  className="rounded-lg p-4 mb-6 border"
                  style={{
                    backgroundColor: currentTheme.colors['secondary-bg'],
                    borderColor: currentTheme.colors['border-color']
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div 
                      className="text-sm font-semibold"
                      style={{ color: currentTheme.colors['text-primary'] }}
                    >
                      🏆 Milestone ถัดไป: {nextMilestone} วัน
                    </div>
                    <div 
                      className="text-sm font-bold"
                      style={{ color: currentTheme.colors.accent }}
                    >
                      เหลือ {daysToMilestone} วัน
                    </div>
                  </div>
                  <div 
                    className="w-full rounded-full h-2"
                    style={{ backgroundColor: currentTheme.colors['border-color'] }}
                  >
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(streak / nextMilestone) * 100}%`,
                        background: `linear-gradient(90deg, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`
                      }}
                    ></div>
                  </div>
                  <div 
                    className="text-xs mt-2"
                    style={{ color: currentTheme.colors['text-secondary'] }}
                  >
                    รางวัลพิเศษ: {calculateDailyBonus(nextMilestone - 1).toLocaleString()} 🎁
                  </div>
                </div>
              )}

              {/* Milestone Info */}
              <div 
                className="rounded-lg p-4 mb-6 border"
                style={{
                  backgroundColor: currentTheme.colors['secondary-bg'],
                  borderColor: currentTheme.colors['border-color']
                }}
              >
                <h4 
                  className="text-sm font-semibold mb-2"
                  style={{ color: currentTheme.colors['text-primary'] }}
                >
                  💡 ข้อมูลโบนัส
                </h4>
                <div 
                  className="text-xs space-y-1"
                  style={{ color: currentTheme.colors['text-secondary'] }}
                >
                  <div>• วันแรก: 10 🎁</div>
                  <div>• เพิ่ม 5 🎁 ทุกวัน</div>
                  <div>• วันที่ 7: 50 🎁 (Milestone!)</div>
                  <div>• วันที่ 14: 100 🎁 (Milestone!)</div>
                  <div>• วันที่ 30: 200 🎁 (Milestone!)</div>
                  <div 
                    className="mt-2 font-semibold"
                    style={{ color: currentTheme.colors['text-primary'] }}
                  >
                    ⚠️ ถ้าข้ามวัน Streak จะรีเซ็ต!
                  </div>
                </div>
              </div>

              {/* Claim Button */}
              {canClaim ? (
                <button
                  onClick={handleClaim}
                  disabled={isClaiming}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all transform ${
                    isClaiming
                      ? 'cursor-not-allowed opacity-50'
                      : isMilestoneDay
                      ? 'hover:scale-105 shadow-lg animate-pulse'
                      : 'hover:scale-105 shadow-lg'
                  }`}
                  style={{
                    background: isClaiming
                      ? currentTheme.colors['border-color']
                      : isMilestoneDay
                      ? `linear-gradient(135deg, ${currentTheme.colors.accent}, ${currentTheme.colors.primary})`
                      : `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`,
                    color: '#FFFFFF'
                  }}
                >
                  {isClaiming ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      กำลังรับโบนัส...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <span>🎁</span>
                      <span>รับโบนัส {bonusAmount.toLocaleString()} 🎁</span>
                      {isMilestoneDay && <span>🏆</span>}
                    </span>
                  )}
                </button>
              ) : (
                <div 
                  className="rounded-xl p-4 text-center"
                  style={{ backgroundColor: currentTheme.colors['secondary-bg'] }}
                >
                  <div 
                    className="font-semibold mb-2"
                    style={{ color: currentTheme.colors.primary }}
                  >
                    ✅ รับโบนัสแล้ววันนี้
                  </div>
                  <div 
                    className="text-sm"
                    style={{ color: currentTheme.colors['text-secondary'] }}
                  >
                    กลับมาใหม่พรุ่งนี้เพื่อรับโบนัสและเพิ่ม Streak!
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div 
                  className="rounded-lg p-3 text-center"
                  style={{ backgroundColor: currentTheme.colors['secondary-bg'] }}
                >
                  <div 
                    className="text-2xl font-bold"
                    style={{ color: currentTheme.colors.primary }}
                  >
                    {streak}
                  </div>
                  <div 
                    className="text-xs"
                    style={{ color: currentTheme.colors['text-secondary'] }}
                  >
                    วันติดต่อกัน
                  </div>
                </div>
                <div 
                  className="rounded-lg p-3 text-center"
                  style={{ backgroundColor: currentTheme.colors['secondary-bg'] }}
                >
                  <div 
                    className="text-2xl font-bold"
                    style={{ color: currentTheme.colors.accent }}
                  >
                    {calculateDailyBonus(streak).toLocaleString()}
                  </div>
                  <div 
                    className="text-xs"
                    style={{ color: currentTheme.colors['text-secondary'] }}
                  >
                    โบนัสวันนี้
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DailyBonusModal;

